import type { KeycloakConfig } from './config'

const REFRESH_SESSION_STORAGE_KEY = 'spectreiq_refresh_session'

export interface RefreshSession {
  refreshToken: string
  realm: string
  tenantCode: string | null
  savedAt: number
}

let currentRefreshSession: RefreshSession | null = null

function getBrowserStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window.localStorage
  } catch {
    return null
  }
}

function readStoredRefreshSession(): RefreshSession | null {
  const storage = getBrowserStorage()
  if (!storage) {
    return null
  }

  const rawSession = storage.getItem(REFRESH_SESSION_STORAGE_KEY)
  if (!rawSession) {
    return null
  }

  try {
    const parsed = JSON.parse(rawSession) as Partial<RefreshSession>
    if (!parsed.refreshToken || !parsed.realm) {
      storage.removeItem(REFRESH_SESSION_STORAGE_KEY)
      return null
    }

    return {
      refreshToken: parsed.refreshToken,
      realm: parsed.realm,
      tenantCode: parsed.tenantCode ?? null,
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : Date.now(),
    }
  } catch {
    storage.removeItem(REFRESH_SESSION_STORAGE_KEY)
    return null
  }
}

function writeStoredRefreshSession(session: RefreshSession | null): void {
  const storage = getBrowserStorage()
  if (!storage) {
    return
  }

  try {
    if (!session) {
      storage.removeItem(REFRESH_SESSION_STORAGE_KEY)
      return
    }

    storage.setItem(REFRESH_SESSION_STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Keep the in-memory session for the active page if persistent storage is blocked.
  }
}

export function getRefreshSession(): RefreshSession | null {
  if (currentRefreshSession) {
    return currentRefreshSession
  }

  currentRefreshSession = readStoredRefreshSession()
  return currentRefreshSession
}

export function getRefreshToken(): string | null {
  return getRefreshSession()?.refreshToken ?? null
}

export function setRefreshToken(
  token: string | null,
  config?: Pick<KeycloakConfig, 'realm' | 'tenantCode'>
): void {
  if (!token || !config?.realm) {
    clearRefreshToken()
    return
  }

  currentRefreshSession = {
    refreshToken: token,
    realm: config.realm,
    tenantCode: config.tenantCode ?? null,
    savedAt: Date.now(),
  }
  writeStoredRefreshSession(currentRefreshSession)
}

export function clearRefreshToken(): void {
  currentRefreshSession = null
  writeStoredRefreshSession(null)
}
